import { ageFromBirthYear, thisYear } from '@muzly/voice-engine';
import type { SurveyAnswers } from '@muzly/voice-engine';

import type { OnboardingAnswer, OnboardingStepId } from './flow';

/**
 * survey — 설문 단계(닉네임·성별·나이·경험·워너비·목적·환경·컨디션)의 순수 설정 + 검증.
 *
 * 화면 문구는 i18n 키로만 담는다(ko/en은 `libs/shared/src/i18n`) — 이 파일에 사람이
 * 읽는 문자열은 없다. 렌더는 SurveyStep이, CTA 활성화 판정은 isSurveyStepAnswered가
 * 한다. React 의존 없음 — vitest 대상.
 */

/**
 * `year`는 값 하나를 고른다는 점에서 `single`과 같고, **렌더만** 다르다 —
 * 70개 버튼 대신 셀렉트. `text`는 선택지가 없는 자유입력(닉네임)이다.
 * 그래서 검증(isSurveyStepAnswered)에서 year는 single과 한 갈래를 탄다.
 */
export type SurveyKind = 'single' | 'multi' | 'year' | 'text';

export interface SurveyOption {
  value: string;
  /**
   * i18n 키. 연도처럼 값이 문구에 들어가는 옵션은 `{{value}}`로 보간한다 — 인자는
   * `optionLabelParams`가 만든다(출생연도는 `{{age}}`도 싣는다).
   */
  labelKey: string;
}

/** 설문으로 다루는 단계만(온보딩 전체 단계의 부분집합). */
export type SurveyStepId = Extract<
  OnboardingStepId,
  | 'nick'
  | 'gender'
  | 'birthYear'
  | 'experience'
  | 'style'
  | 'purpose'
  | 'environment'
  | 'condition'
>;

export interface SurveyStepConfig {
  id: SurveyStepId;
  kind: SurveyKind;
  titleKey: string;
  subtitleKey?: string;
  /** 선택지 — `text`는 고를 게 없으므로 빈 배열. */
  options: SurveyOption[];
  /** 입력 힌트 — `text`의 placeholder, `year` 셀렉트의 미선택 항목. */
  placeholderKey?: string;
  /** `text` 전용 길이 상한 — 이후 화면 제목에 그대로 박히는 값이라 묶어둔다. */
  maxLength?: number;
  /**
   * 이 값 선택 시 자유입력 텍스트를 노출·요구한다. 텍스트 응답은 `${id}Other`
   * 키(otherKey)에 저장된다.
   */
  otherValue?: string;
  /**
   * 선택지 라벨에 `{{value}}` 말고 더 싣는 인자 — 그릴 때 부른다(올해가 바뀌면 값도 바뀐다).
   * 출생연도는 나이를 싣는다(TestFlight #55).
   */
  labelParams?: (
    option: SurveyOption,
    currentYear: number,
  ) => Record<string, string | number>;
}

/**
 * 선택 가능한 출생연도 범위 — landing 진단 설문(`SURVEY_YEAR_MIN/MAX`)과 같은 창.
 * 최신 연도가 위로 오게 내림차순으로 낸다.
 */
const BIRTH_YEAR_MIN = 1945;
const BIRTH_YEAR_MAX = 2014;

const birthYearOptions = (): SurveyOption[] =>
  Array.from({ length: BIRTH_YEAR_MAX - BIRTH_YEAR_MIN + 1 }, (_, i) => ({
    value: String(BIRTH_YEAR_MAX - i),
    labelKey: 'web.onboarding.birthYear.option',
  }));

/**
 * 선택지 라벨의 i18n 인자 — 값(`{{value}}`)에 그 단계가 더 싣는 인자(`labelParams`)를 얹는다.
 * 설문 화면과 프로필 화면이 이 함수 하나를 거쳐야 같은 선택지를 같은 말로 부른다.
 */
export const optionLabelParams = (
  step: Pick<SurveyStepConfig, 'labelParams'>,
  option: SurveyOption,
  currentYear: number = thisYear(),
): Record<string, string | number> => ({
  value: option.value,
  ...step.labelParams?.(option, currentYear),
});

/** 닉네임 길이 상한 — 이후 스텝 제목("<닉네임>님의 나이를…")이 한 줄에 들어가는 선. */
const NICKNAME_MAX_LENGTH = 12;

const option = (value: string, step: string): SurveyOption => ({
  value,
  labelKey: `web.onboarding.${step}.options.${value}`,
});

export const SURVEY_STEPS: SurveyStepConfig[] = [
  {
    id: 'nick',
    kind: 'text',
    titleKey: 'web.onboarding.nick.title',
    subtitleKey: 'web.onboarding.nick.subtitle',
    placeholderKey: 'web.onboarding.nick.placeholder',
    maxLength: NICKNAME_MAX_LENGTH,
    options: [],
  },
  {
    id: 'gender',
    kind: 'single',
    titleKey: 'web.onboarding.gender.title',
    subtitleKey: 'web.onboarding.gender.subtitle',
    // 'none'은 엔진이 성별중립 경로로 읽는 값(landing consts.ts와 동일).
    options: ['female', 'male', 'none'].map((v) => option(v, 'gender')),
  },
  {
    id: 'birthYear',
    kind: 'year',
    titleKey: 'web.onboarding.birthYear.title',
    subtitleKey: 'web.onboarding.birthYear.subtitle',
    placeholderKey: 'web.onboarding.birthYear.placeholder',
    options: birthYearOptions(),
    // '출생연도' → '나이'(TestFlight #55). 저장·서버 프로필은 출생연도 그대로 — 나이는 해가
    // 바뀌면 달라지는 값이라 담지 않고 그릴 때 올해로 센다.
    labelParams: (opt, currentYear) => ({
      age: ageFromBirthYear(opt.value, currentYear) ?? '',
    }),
  },
  {
    id: 'experience',
    kind: 'single',
    titleKey: 'web.onboarding.experience.title',
    options: ['first', 'hobby', 'lesson', 'pro'].map((v) =>
      option(v, 'experience'),
    ),
  },
  {
    id: 'style',
    kind: 'single',
    titleKey: 'web.onboarding.style.title',
    subtitleKey: 'web.onboarding.style.subtitle',
    options: ['powerful', 'ballad', 'pop', 'classic', 'jazz'].map((v) =>
      option(v, 'style'),
    ),
  },
  {
    id: 'purpose',
    kind: 'single',
    titleKey: 'web.onboarding.purpose.title',
    options: ['curious', 'check', 'track', 'audition'].map((v) =>
      option(v, 'purpose'),
    ),
  },
  {
    id: 'environment',
    kind: 'single',
    titleKey: 'web.onboarding.environment.title',
    subtitleKey: 'web.onboarding.environment.subtitle',
    options: ['quiet', 'noisy', 'outdoor'].map((v) => option(v, 'environment')),
  },
  {
    id: 'condition',
    kind: 'single',
    titleKey: 'web.onboarding.condition.title',
    options: ['best', 'normal', 'husky', 'bad'].map((v) =>
      option(v, 'condition'),
    ),
  },
];

export const surveyStepById = (
  id: OnboardingStepId,
): SurveyStepConfig | undefined => SURVEY_STEPS.find((s) => s.id === id);

/** 자유입력 텍스트 응답 키 — 예: condition → conditionOther. */
export const otherKey = (id: SurveyStepId): string => `${id}Other`;

/** 답 맵에서 주어진 키만 골라낸다 — 없는 키는 빠진다(undefined로 남기지 않는다). */
export const pickAnswers = (
  answers: Record<string, OnboardingAnswer>,
  keys: readonly string[],
): Record<string, OnboardingAnswer> => {
  const picked: Record<string, OnboardingAnswer> = {};
  for (const key of keys) {
    if (answers[key] !== undefined) {
      picked[key] = answers[key];
    }
  }
  return picked;
};

/** 자유입력(text) 충족 — 공백만은 답이 아니고, 상한을 넘는 길이도 받지 않는다. */
const isTextAnswered = (
  config: SurveyStepConfig,
  value: OnboardingAnswer | undefined,
): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  return (
    trimmed.length > 0 &&
    (config.maxLength === undefined || trimmed.length <= config.maxLength)
  );
};

/**
 * 필수 응답 충족 여부. text=공백 아닌 입력, single=값 존재, multi=1개 이상 선택.
 * otherValue를 고른 경우 자유입력(`${id}Other`)이 공백이 아니어야 충족으로 본다.
 */
export const isSurveyStepAnswered = (
  config: SurveyStepConfig,
  answers: Record<string, OnboardingAnswer>,
): boolean => {
  const value = answers[config.id];
  const otherText = answers[otherKey(config.id)];
  const otherFilled =
    typeof otherText === 'string' && otherText.trim().length > 0;

  if (config.kind === 'text') {
    return isTextAnswered(config, value);
  }

  // year는 값 하나를 고르는 점에서 single과 같다.
  if (config.kind !== 'multi') {
    if (typeof value !== 'string' || value.length === 0) {
      return false;
    }
    return config.otherValue === value ? otherFilled : true;
  }

  const selected = Array.isArray(value) ? value : [];
  if (selected.length === 0) {
    return false;
  }
  return config.otherValue !== undefined && selected.includes(config.otherValue)
    ? otherFilled
    : true;
};

/**
 * 온보딩 응답 → 엔진의 SurveyAnswers. 엔진은 단일 문자열만 받으므로 다중 선택
 * 응답(배열)은 넘기지 않는다. 답이 없으면 **빈 문자열 대신 undefined**로 남긴다 —
 * `estimateVocalAge`가 `birthYear ? ... : null`로 갈라져, 빈 문자열을 넘기면
 * "모름"이 되는 대신 나이 계산이 조용히 falsy 경로를 타기 때문.
 */
export const toSurveyAnswers = (
  answers: Record<string, OnboardingAnswer>,
): SurveyAnswers => {
  const single = (id: string): string | undefined => {
    const value = answers[id];
    return typeof value === 'string' ? value : undefined;
  };
  return { gender: single('gender'), birthYear: single('birthYear') };
};
